<template>
  <div class="contract-request-upsert">
    <div class="row">
      <div class="col-md-12">
        <KTCodePreview v-bind:title="pageTitle">
          <template v-slot:toolbar>
            <div class="row">
              <div class="ml-3">
                <b-button size="sm" variant="secondary" @click="goBack">
                  <i style="font-size: 1rem" class="fas fa-arrow-left"></i>
                  <b>Về danh sách</b>
                </b-button>
              </div>
            </div>
          </template>

          <template v-slot:preview>
            <b-alert v-if="loadError" variant="danger" show>
              {{ loadError }}
            </b-alert>
            <b-alert v-if="submitError" variant="danger" show>
              {{ submitError }}
            </b-alert>
            <b-alert
              v-if="isFormLocked"
              :variant="isReviewMode ? 'warning' : 'info'"
              show
            >
              <i
                :class="[
                  'mr-1',
                  isReviewMode ? 'fas fa-eye' : 'fas fa-info-circle',
                ]"
                style="color: inherit !important"
              ></i>
              {{ readOnlyNotice }}
            </b-alert>

            <!-- Link sang HĐ đã tạo từ Đề nghị này (nếu đã convert) -->
            <b-alert v-if="linkedContractId" variant="success" show>
              <i class="fas fa-link mr-1" style="color: inherit !important"></i>
              <b>HĐ đã tạo:</b>
              <span
                class="ml-1 cr-linked-contract"
                @click="goToContract(linkedContractId)"
              >
                {{ linkedContractNumber || linkedContractId }}
                <i class="fas fa-external-link-alt ml-1"></i>
              </span>
            </b-alert>

            <!-- ĐN bị từ chối / hủy → hiện rõ lý do (BE lưu rejectedReason /
                 cancelReason). Trước đây chỉ có status, user không thấy lý do. -->
            <b-alert v-if="cancelOrRejectReason" variant="danger" show>
              <i class="fas fa-ban mr-1" style="color: inherit !important"></i>
              <b>{{ cancelOrRejectReason.label }}:</b>
              {{ cancelOrRejectReason.text }}
            </b-alert>

            <fieldset :disabled="isFormLocked" class="border-0 p-0 m-0">
              <b-container class="bv-example-row">
                <!-- ===== Thông tin khách hàng — component dùng chung ===== -->
                <CustomerInfoSection
                  v-if="loadingComponent"
                  :customer="customerProxy"
                  :disabled="isFormLocked"
                  :errors="legalErrors"
                  :appellation-options="appellationOptions"
                  :customer-type-options="customerTypeOptions"
                  :dp-config="dpConfigs.date"
                  editable-type
                  :disabled-type="isEdit"
                  show-required
                >
                  <!-- SĐT cá nhân — Autosuggest customers/get-by-phone -->
                  <template #phone-customer>
                    <Autosuggest
                      :model="contractData.customerPhone"
                      :suggestions="filteredCustomerOptions"
                      :suggestionName="'phoneNumber'"
                      placeholder="số điện thoại"
                      :limit="10"
                      @select="onSelectCustomer"
                      @change="onSearchCustomerChange"
                    >
                      <template #custom="{ suggestion }">
                        <div class="d-flex flex-column">
                          <span
                            v-if="suggestion.item.phoneNumber"
                            class="text-primary mb-1"
                            >({{ suggestion.item.phoneNumber }})</span
                          >
                          <span v-if="suggestion.item.fullName">{{
                            suggestion.item.fullName
                          }}</span>
                          <span v-else class="text-muted">{{
                            suggestion.item.message
                          }}</span>
                        </div>
                      </template>
                    </Autosuggest>
                  </template>
                  <!-- KH DN — Autosuggest businesses?search (BE match name/email/phoneNo,
                     KHÔNG match taxNo — xem PIT-F-008). Placeholder phản ánh thực tế
                     những gì gõ ra match được. -->
                  <template #mst-business>
                    <Autosuggest
                      :model="businessTaxNoInput"
                      :suggestions="filteredBusinessOptions"
                      :suggestionName="'taxNo'"
                      placeholder="Mã số thuế"
                      :limit="10"
                      @select="onSelectBusiness"
                      @change="onSearchBusinessChange"
                    >
                      <template #custom="{ suggestion }">
                        <div class="d-flex flex-column">
                          <span
                            v-if="suggestion.item.taxNo"
                            class="text-primary mb-1"
                            >({{ suggestion.item.taxNo }})</span
                          >
                          <span v-if="suggestion.item.name">{{
                            suggestion.item.name
                          }}</span>
                          <span v-else class="text-muted">{{
                            suggestion.item.message
                          }}</span>
                        </div>
                      </template>
                    </Autosuggest>
                  </template>
                </CustomerInfoSection>

                <!-- ===== Section 2: Thông tin Đề nghị ===== -->
                <hr
                  class="hr-text"
                  data-content="Thông tin Đề nghị"
                  style="font-weight: 600"
                />
                <b-row v-if="loadingComponent">
                  <b-col cols="4">
                    <ContractInfo
                      :contractData="contractData"
                      :action="action"
                      :isDisabled="false"
                    />
                  </b-col>
                  <b-col cols="4">
                    <EmployeeInfo
                      :storeId="contractData.storeId"
                      :contractData="contractData"
                      :contractDataEmployeeInfo="contractEmployeeInfo"
                      :action="action"
                      :isDisabled="false"
                    />
                  </b-col>
                  <b-col cols="4">
                    <Discount
                      :contractData="contractData"
                      :action="action"
                      :isDisabled="false"
                    />
                  </b-col>
                </b-row>

                <!-- ===== Section 3: Thông tin sản phẩm ===== -->
                <hr
                  class="hr-text"
                  data-content="Thông tin sản phẩm"
                  style="font-weight: 600"
                />
                <ProductTable
                  v-if="loadingComponent"
                  :editable="true"
                  :isDisabled="false"
                  :contractData="contractData"
                />

                <!-- ===== Section 4: Đợt thanh toán (chỉ INSTALLMENT) ===== -->
                <template
                  v-if="
                    loadingComponent &&
                    contractData.paymentType === 'INSTALLMENT'
                  "
                >
                  <hr
                    class="hr-text"
                    data-content="Đợt thanh toán"
                    style="font-weight: 600"
                  />
                  <PaymentTerm
                    :contractData="contractData"
                    :isDisabled="false"
                  />
                </template>

                <!-- ===== Section 5: Ghi chú ===== -->
                <hr
                  class="hr-text"
                  data-content="Ghi chú"
                  style="font-weight: 600"
                />
                <b-row>
                  <b-col cols="6">
                    <b-form-group>
                      <label>Ghi chú khách hàng:</label>
                      <b-form-textarea
                        v-model="customerNote"
                        size="sm"
                        :rows="3"
                        placeholder="Ghi chú KH"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group>
                      <label>Ghi chú nội bộ:</label>
                      <b-form-textarea
                        v-model="internalNote"
                        size="sm"
                        :rows="3"
                        placeholder="Ghi chú nội bộ"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-container>
            </fieldset>
          </template>

          <template v-slot:foot>
            <!-- Review mode: nút Duyệt thay nút Cập nhật -->
            <b-button
              v-if="isReviewMode"
              class="font-weight-bolder mr-3"
              size="sm"
              variant="success"
              style="width: 110px"
              :disabled="approving"
              @click="onApproveReview"
            >
              <i class="fas fa-check mr-1"></i>
              Duyệt
            </b-button>
            <!-- Pending bình thường: nút Cập nhật -->
            <b-button
              v-else-if="!isReadOnly"
              class="font-weight-bolder mr-3"
              size="sm"
              variant="primary"
              style="min-width: 110px"
              :disabled="submitting"
              @click="onSubmit"
            >
              {{ submitButtonLabel }}
            </b-button>
            <b-button
              class="font-weight-bolder"
              size="sm"
              variant="secondary"
              :style="isFormLocked ? 'width: 110px' : 'width: 80px'"
              @click="goBack"
            >
              {{ isFormLocked ? 'Đóng' : 'Hủy' }}
            </b-button>
          </template>
        </KTCodePreview>
      </div>
    </div>
  </div>
</template>

<script>
import { get } from 'lodash';
import moment from 'moment';
import Swal from 'sweetalert2';
// Datepicker icons setup + datePicker component đã chuyển sang CustomerInfoSection.

import KTCodePreview from '@/view/content/CodePreview.vue';
import ContractRequestService from '@/core/services/contractRequest.service';
import ApiService from '@/core/services/api.service';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import {
  CUSTOMER_TYPE,
  CUSTOMER_TYPE_OPTIONS,
  CONTRACT_REQUEST_STATUS,
  CONTRACT_REQUEST_STATUS_LABEL,
} from '@/core/constants/contractRequest';
import { makeToastSuccess, makeToastFaile, unMaskPrice } from '@/utils/common';
import { ITEM_TYPE } from '@/utils/enum';

// Reuse shared components legacy (giống Upsert.vue HĐ legacy).
import CustomerInfoSection from '@/view/components/contracts/CustomerInfoSection';
import ContractInfo from '@/view/components/contracts/info';
import EmployeeInfo from '@/view/components/contracts/EmployeeInfo';
import Discount from '@/view/components/contracts/Discount';
import PaymentTerm from '@/view/components/contracts/PaymentTerm';
import ProductTable from '@/view/components/contracts/ProductTable';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import debounce from 'debounce';
import { TIME_TRIGGER } from '@/utils/constants';

export default {
  name: 'ContractRequestUpsert',
  components: {
    KTCodePreview,
    CustomerInfoSection,
    ContractInfo,
    EmployeeInfo,
    Discount,
    PaymentTerm,
    ProductTable,
    Autosuggest,
  },
  props: {
    id: { type: [String, Number], default: null },
  },
  data() {
    return {
      // Cấu hình datepicker — pattern AddCustomer.vue
      dpConfigs: {
        date: {
          format: 'DD/MM/YYYY',
          useCurrent: false,
          showClear: true,
          showClose: true,
          minDate: false,
          maxDate: false,
          locale: 'vi',
          sideBySide: true,
        },
      },
      submitting: false,
      approving: false,
      submitError: '',
      loadError: '',
      loadingComponent: false,
      action: 'create',
      requestStatus: null,
      // Lý do từ chối / hủy ĐN (BE lưu rejectedReason / cancelReason khi
      // reject() / cancelManual()). Trước đây UI không hiện → user không thấy.
      requestRejectedReason: null,
      requestCancelReason: null,
      linkedContractId: null,
      linkedContractNumber: null,
      // contractData dùng schema legacy để feed vào shared components contracts/*.
      // Mapping với BE ContractRequest schema chỉ ở loadDetail() và buildPayload().
      contractData: {
        id: null,
        // Store
        storeId: null,
        store: { id: null, name: null },
        // Contract meta
        contractNumber: null, // Đề nghị KHÔNG có contractNumber, dùng tạm để info.vue render
        contractDate: null,
        expectedDeliveryDate: null,
        shippingAddress: null,
        // Customer
        customerId: null,
        customerBusinessId: null,
        customerType: 'CUSTOMER',
        customerName: null,
        customerPhone: null,
        customerAddress: null,
        customerGender: null,
        // Sale & Shipper
        saleId: null,
        saleName: null,
        shipperId: null,
        shipperName: null,
        // Phase B — 4 vai trò NV mở rộng (bán xe). Reactivity tracking + carry
        // qua chain ĐN → HĐ → Bill (xem migration AddSalesRolesToContractAndRequest).
        technicalId: null,
        technicalName: null,
        cashierId: null,
        cashierName: null,
        managerId: null,
        managerName: null,
        assistantId: null,
        assistantName: null,
        // Payment + Discount
        paymentType: 'DIRECTLY',
        discountType: 'AMOUNT',
        totalDiscountAmount: 0,
        // Items + Payment terms — keys mà ProductTable + PaymentTerm component dùng
        contractDetail: [],
        contractPaymentTerm: [],
      },
      contractEmployeeInfo: {
        saleId: null,
        saleName: '',
        shipperId: null,
        shipperName: '',
        // Phase B — 4 vai trò NV mở rộng (xem comment ở contractData trên).
        technicalId: null,
        technicalName: '',
        cashierId: null,
        cashierName: '',
        managerId: null,
        managerName: '',
        assistantId: null,
        assistantName: '',
      },
      // Sprint 3 BE-only fields (không thuộc legacy schema)
      customerBusinessId: null,
      customerEmail: '',
      customerNote: '',
      internalNote: '',
      // ===== Legal info section (chuyển từ modal Tạo HĐ sang form Đề nghị) =====
      // Pháp lý KH cá nhân (4 field) hoặc DN (5 field) — prefill từ Customer/Business hiện có.
      customerLegal: {
        cccdNo: '',
        cccdDate: '',
        cccdPlace: '',
        phone2: '',
        dob: '',
      },
      businessLegal: {
        representative: '',
        repPosition: '',
        foundDate: '',
        authorizationNo: '',
        authorizationDate: '',
      },
      // Snapshot full customer/business để merge khi PUT (BE require fullName/phoneNumber/...)
      customerSnapshot: null,
      businessSnapshot: null,
      // Tracking customerId/customerBusinessId đã load → tránh re-fetch.
      legalLoadedCustomerId: null,
      legalLoadedBusinessId: null,
      // Lỗi inline cho field pháp lý
      legalErrors: {},
      // ===== Inline customer info section state =====
      // Type-switcher options + dropdown options (refactor v4)
      customerTypeOptions: CUSTOMER_TYPE_OPTIONS.map((o) => ({
        value: o.value,
        text: o.text,
      })),
      appellationOptions: [
        { value: null, text: 'Chọn danh xưng' },
        { value: 1, text: 'Anh' },
        { value: 2, text: 'Chị' },
        { value: 3, text: 'Khác' },
      ],
      // Autosuggest state — cá nhân
      filteredCustomerOptions: [],
      // Autosuggest state — DN
      businessTaxNoInput: '',
      filteredBusinessOptions: [],
    };
  },
  computed: {
    isEdit() {
      return !!this.id;
    },
    // ĐN bị từ chối / hủy → trả { label, text } để hiện banner lý do.
    cancelOrRejectReason() {
      if (
        this.requestStatus === CONTRACT_REQUEST_STATUS.REJECTED &&
        this.requestRejectedReason
      ) {
        return {
          label: 'Lý do từ chối Đề nghị',
          text: this.requestRejectedReason,
        };
      }
      if (
        this.requestStatus === CONTRACT_REQUEST_STATUS.CANCELLED_MANUAL &&
        this.requestCancelReason
      ) {
        return { label: 'Lý do hủy Đề nghị', text: this.requestCancelReason };
      }
      return null;
    },
    /**
     * Proxy object mapping state rải rác (contractData / customerLegal / businessLegal /
     * customerEmail / businessTaxNoInput) → unified `customer*` shape mà
     * CustomerInfoSection mong đợi. Getter/setter forward 2 chiều — component mutate
     * `customer.xxx` sẽ ghi vào đúng nơi.
     */
    customerProxy() {
      const v = this;
      return {
        get customerType() {
          return v.contractData.customerType;
        },
        set customerType(x) {
          v.contractData.customerType = x;
        },
        get customerPhone() {
          return v.contractData.customerPhone;
        },
        set customerPhone(x) {
          v.contractData.customerPhone = x;
        },
        get customerName() {
          return v.contractData.customerName;
        },
        set customerName(x) {
          v.contractData.customerName = x;
        },
        get customerGender() {
          return v.contractData.customerGender;
        },
        set customerGender(x) {
          v.contractData.customerGender = x;
        },
        get customerAddress() {
          return v.contractData.customerAddress;
        },
        set customerAddress(x) {
          v.contractData.customerAddress = x;
        },
        get customerEmail() {
          return v.customerEmail;
        },
        set customerEmail(x) {
          v.customerEmail = x;
        },
        get customerDob() {
          return v.customerLegal.dob;
        },
        set customerDob(x) {
          v.customerLegal.dob = x;
        },
        get customerCccdNo() {
          return v.customerLegal.cccdNo;
        },
        set customerCccdNo(x) {
          v.customerLegal.cccdNo = x;
        },
        get customerCccdDate() {
          return v.customerLegal.cccdDate;
        },
        set customerCccdDate(x) {
          v.customerLegal.cccdDate = x;
        },
        get customerCccdPlace() {
          return v.customerLegal.cccdPlace;
        },
        set customerCccdPlace(x) {
          v.customerLegal.cccdPlace = x;
        },
        // DN
        get customerMst() {
          return v.businessTaxNoInput;
        },
        set customerMst(x) {
          v.businessTaxNoInput = x;
        },
        get customerFoundDate() {
          return v.businessLegal.foundDate;
        },
        set customerFoundDate(x) {
          v.businessLegal.foundDate = x;
        },
        get customerRepresentative() {
          return v.businessLegal.representative;
        },
        set customerRepresentative(x) {
          v.businessLegal.representative = x;
        },
        get customerRepPosition() {
          return v.businessLegal.repPosition;
        },
        set customerRepPosition(x) {
          v.businessLegal.repPosition = x;
        },
        get authorizationNo() {
          return v.businessLegal.authorizationNo;
        },
        set authorizationNo(x) {
          v.businessLegal.authorizationNo = x;
        },
        get authorizationDate() {
          return v.businessLegal.authorizationDate;
        },
        set authorizationDate(x) {
          v.businessLegal.authorizationDate = x;
        },
      };
    },
    isReadOnly() {
      // Luồng A — cho sửa khi pending HOẶC rejected (resubmit: từ chối → sửa →
      // gửi duyệt lại). Các trạng thái khác (approved / contract_created /
      // cancelled_manual) vẫn chỉ-xem. BE enforce ownership (TVBH owner).
      return (
        this.isEdit &&
        this.requestStatus !== null &&
        this.requestStatus !== CONTRACT_REQUEST_STATUS.PENDING &&
        this.requestStatus !== CONTRACT_REQUEST_STATUS.REJECTED
      );
    },
    /**
     * Review mode — user click Duyệt từ list, navigate detail với `?action=review`.
     * Form khoá (giống read-only) nhưng nút "Cập nhật" đổi thành "Duyệt" để user
     * review thông tin lần cuối trước khi confirm duyệt.
     */
    isReviewMode() {
      return (
        this.isEdit &&
        this.$route.query.action === 'review' &&
        this.requestStatus === CONTRACT_REQUEST_STATUS.PENDING
      );
    },
    /**
     * Form bị disable khi: đang review (chờ duyệt) HOẶC trạng thái không phải pending.
     */
    isFormLocked() {
      return this.isReadOnly || this.isReviewMode;
    },
    pageTitle() {
      if (!this.isEdit) return 'Tạo Đề nghị HĐ';
      if (this.isReviewMode) return 'Duyệt Đề nghị HĐ';
      return this.isReadOnly ? 'Chi tiết Đề nghị HĐ' : 'Sửa Đề nghị HĐ';
    },
    /**
     * Nhãn nút submit: ĐN bị từ chối → "Gửi duyệt lại" (resubmit về pending),
     * còn lại giữ "Cập nhật" / "Tạo Đề nghị".
     */
    submitButtonLabel() {
      if (!this.isEdit) return 'Tạo Đề nghị';
      return this.requestStatus === CONTRACT_REQUEST_STATUS.REJECTED
        ? 'Gửi duyệt lại'
        : 'Cập nhật';
    },
    readOnlyNotice() {
      if (this.isReviewMode) {
        return 'Vui lòng kiểm tra lại thông tin trước khi bấm Duyệt.';
      }
      if (!this.isReadOnly) return '';
      const label =
        CONTRACT_REQUEST_STATUS_LABEL[this.requestStatus] || this.requestStatus;
      return `Đề nghị đang ở trạng thái "${label}" — chỉ xem, không chỉnh sửa được.`;
    },
  },
  watch: {
    /**
     * Khi user pick KH qua autocomplete (CustomerInfo emit mutate
     * contractData.customerId trực tiếp), auto-load detail Customer
     * → prefill 4 field pháp lý.
     */
    'contractData.customerId'(newId, oldId) {
      if (!newId || newId === oldId) return;
      if (newId === this.legalLoadedCustomerId) return;
      this.loadCustomerDetail(newId);
    },
    /**
     * Khi user chọn DN (chưa có UI nhưng để dự phòng cho mode edit
     * Đề nghị có DN). DN load qua endpoint riêng.
     */
    customerBusinessId(newId, oldId) {
      this.contractData.customerBusinessId = newId || null;
      if (!newId || newId === oldId) return;
      if (newId === this.legalLoadedBusinessId) return;
      this.loadBusinessDetail(newId);
    },
    /**
     * Khi user switch customerType (cá nhân ↔ DN) → reset legal state để
     * tránh lẫn dữ liệu giữa 2 loại.
     */
    'contractData.customerType'() {
      this.legalErrors = {};
    },
  },
  created() {
    this.action = this.id ? 'update' : 'create';
    if (this.isEdit) {
      this.loadDetail();
    } else {
      // Auto-fill từ user info nếu có
      this.loadInitialUser();
      this.loadingComponent = true;
    }
  },
  mounted() {
    this.updateBreadcrumb();
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'list-contract-request' });
    },
    goToContract(id) {
      if (!id) return;
      this.$router.push({
        name: 'edit-contract',
        params: { id },
      });
    },
    updateBreadcrumb() {
      let leaf;
      if (!this.isEdit) leaf = 'Tạo Đề nghị';
      else if (this.isReviewMode) leaf = 'Duyệt Đề nghị';
      else leaf = this.isReadOnly ? 'Chi tiết Đề nghị' : 'Sửa Đề nghị';
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Đề nghị tạo HĐ', route: '/contract-requests' },
        { title: leaf },
      ]);
    },
    /**
     * Review mode: confirm rồi gọi approve API. Sau khi xong → navigate về list.
     */
    async onApproveReview() {
      const customerName = this.contractData.customerName || '';
      const result = await Swal.fire({
        title: 'Duyệt đề nghị tạo hợp đồng',
        html: `Duyệt Đề nghị của khách <b>${customerName}</b>?<br/><span style="color:#e65b5b">Hành động này không thể hoàn tác.</span>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3699ff',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Duyệt',
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
        position: 'center',
      });
      if (!result.value) return;
      this.approving = true;
      try {
        await ContractRequestService.approve(this.id);
        makeToastSuccess('Đã duyệt Đề nghị');
        this.$router.push({ name: 'list-contract-request' });
      } catch (err) {
        makeToastFaile(get(err, 'response.data.message') || 'Không duyệt được');
      } finally {
        this.approving = false;
      }
    },
    loadInitialUser() {
      try {
        const raw = localStorage.getItem('employee_info');
        if (raw) {
          const u = JSON.parse(raw);
          if (u.id) {
            this.contractData.saleId = u.id;
            this.contractData.saleName = u.fullName || u.name || '';
            this.contractEmployeeInfo.saleId = u.id;
            this.contractEmployeeInfo.saleName = u.fullName || u.name || '';
          }
          if (!this.contractData.storeId && u.storeId) {
            this.contractData.storeId = u.storeId;
          }
        }
      } catch (err) {
        // ignore
      }
    },
    // ===================================================================
    // Customer info inline (refactor v4) — autocomplete + select handlers.
    // Pattern theo CustomerInfo shared component (debounce + Autosuggest +
    // ApiService).
    // ===================================================================
    debounceSearchCustomer: debounce(function () {
      this.fetchCustomerSuggestions();
    }, TIME_TRIGGER),
    debounceSearchBusiness: debounce(function () {
      this.fetchBusinessSuggestions();
    }, TIME_TRIGGER),
    onSearchCustomerChange(text) {
      // User gõ trong ô SĐT → reset id (KH chưa được pick) + đẩy text vào
      // contractData để v-model giữ giá trị; debounce gọi BE.
      const value = text || '';
      this.contractData.customerPhone = value;
      this.contractData.customerId = null;
      this.legalLoadedCustomerId = null;
      this.filteredCustomerOptions = [];
      if (!value.trim()) return;
      this.debounceSearchCustomer();
    },
    fetchCustomerSuggestions() {
      const text = (this.contractData.customerPhone || '').trim();
      if (!text) return;
      ApiService.get(`customers/get-by-phone?phoneNumber=${text}`)
        .then((res) => {
          const list = get(res, 'data.data') || [];
          if (list.length) {
            this.filteredCustomerOptions = list;
          } else {
            this.filteredCustomerOptions = [
              { message: 'SĐT không có trong hệ thống', phoneNumber: text },
            ];
          }
        })
        .catch(() => {
          this.filteredCustomerOptions = [];
        });
    },
    onSelectCustomer(option) {
      const item = option && option.item ? option.item : null;
      if (!item) return;
      if (item.id) {
        // KH đã có trong hệ thống → fill các field + load detail (qua watcher
        // contractData.customerId đã có sẵn).
        this.contractData.customerId = item.id;
        this.contractData.customerPhone = item.phoneNumber || '';
        this.contractData.customerName = item.fullName || '';
        this.contractData.customerAddress = item.address || '';
        this.contractData.customerGender = item.gender || null;
      } else {
        // KH mới (suggestion = "không có trong hệ thống") → giữ SĐT, clear
        // các field khác.
        this.contractData.customerId = null;
        this.contractData.customerPhone = item.phoneNumber || '';
      }
    },
    onSearchBusinessChange(text) {
      const value = text || '';
      this.businessTaxNoInput = value;
      this.customerBusinessId = null;
      this.legalLoadedBusinessId = null;
      this.filteredBusinessOptions = [];
      if (!value.trim()) return;
      this.debounceSearchBusiness();
    },
    fetchBusinessSuggestions() {
      const text = (this.businessTaxNoInput || '').trim();
      if (!text) return;
      // BE listCustomerBusiness: search match name/email/phoneNo (KHÔNG match
      // taxNo theo hiện trạng entity). Dùng tạm để autocomplete; user gõ MST
      // sẽ không tìm được DN có sẵn — sẽ tạo mới qua submit (BE auto create
      // business từ payload customerName).
      ApiService.query(`businesses`, {
        params: { search: text, page: 1, limit: 10 },
      })
        .then((res) => {
          const payload = get(res, 'data.data');
          let list = [];
          if (Array.isArray(payload)) list = payload;
          else if (payload && Array.isArray(payload.data)) list = payload.data;
          if (list.length) {
            this.filteredBusinessOptions = list;
          } else {
            this.filteredBusinessOptions = [
              { message: 'DN không có trong hệ thống', taxNo: text },
            ];
          }
        })
        .catch(() => {
          this.filteredBusinessOptions = [];
        });
    },
    onSelectBusiness(option) {
      const item = option && option.item ? option.item : null;
      if (!item) return;
      if (item.id) {
        this.customerBusinessId = item.id;
        this.businessTaxNoInput = item.taxNo || '';
        this.contractData.customerName = item.name || '';
        this.contractData.customerPhone = item.phoneNo || '';
        this.contractData.customerAddress = item.address || '';
        this.customerEmail = item.email || '';
        // Watcher customerBusinessId tự động load businessDetail → prefill
        // businessLegal + snapshot.
      } else {
        this.customerBusinessId = null;
        this.businessTaxNoInput = item.taxNo || '';
      }
    },
    async loadDetail() {
      try {
        const res = await ContractRequestService.getById(this.id);
        const data = get(res, 'data.data') || get(res, 'data');
        if (!data) {
          this.loadError = 'Không tìm thấy Đề nghị';
          this.loadingComponent = true;
          return;
        }
        this.requestStatus = data.status || null;
        this.requestRejectedReason = data.rejectedReason || null;
        this.requestCancelReason = data.cancelReason || null;
        this.linkedContractId = data.contractId || null;
        // BE leftJoinAndMapOne `cr.contract` → mã hợp đồng để hiện trên banner
        this.linkedContractNumber =
          (data.contract && data.contract.contractNumber) || null;
        this.updateBreadcrumb();
        // Map BE Sprint 3 ContractRequest → contractData legacy schema
        this.contractData = {
          id: data.id,
          storeId: data.storeId || null,
          store: { id: data.storeId || null, name: data.store?.name || null },
          contractNumber: data.code || null,
          contractDate: data.contractDate
            ? moment(data.contractDate).format('DD/MM/YYYY')
            : null,
          expectedDeliveryDate: data.expectedDeliveryDate
            ? moment(data.expectedDeliveryDate).format('DD/MM/YYYY')
            : null,
          shippingAddress: data.shippingAddress || null,
          customerId: data.customerId || null,
          customerType: data.customerType || CUSTOMER_TYPE.CUSTOMER,
          customerName: data.customerName || null,
          customerPhone: data.customerPhone || null,
          customerAddress: data.customerAddress || null,
          customerGender: data.customerGender || null,
          saleId: data.saleId || null,
          saleName: data.saleName || null,
          shipperId: data.shipperId || null,
          shipperName: data.shipperName || null,
          // Phase B — 4 vai trò NV mở rộng (bán xe).
          technicalId: data.technicalId || null,
          technicalName: data.technicalName || null,
          cashierId: data.cashierId || null,
          cashierName: data.cashierName || null,
          managerId: data.managerId || null,
          managerName: data.managerName || null,
          assistantId: data.assistantId || null,
          assistantName: data.assistantName || null,
          paymentType: data.paymentType || 'DIRECTLY',
          discountType: data.discountType || 'AMOUNT',
          totalDiscountAmount: data.totalDiscountAmount || 0,
          contractDetail: (data.items || []).flatMap((it) => {
            const mainClientId = this.$uuid.v4();
            const main = {
              id: it.id,
              _clientId: mainClientId,
              productId: it.productId,
              productName: it.productName,
              productCode: it.productCode || it.product?.code || '',
              productPrice: Number(it.productPrice) || 0,
              sellingPrice: Number(it.productPrice) || 0,
              sellingPriceMask: Number(it.productPrice) || 0,
              quantity: Number(it.quantity) || 1,
              discountAmount: Number(it.discountAmount) || 0,
              discountType: it.discountType || 'AMOUNT',
              totalAmount: Number(it.totalAmount) || 0,
              productChassisId: it.productChassisId || null,
              productSerialId: it.productSerialId || null,
              chassisNumber: it.chassisNumber || null,
              engineNumber: it.engineNumber || null,
              serialNumber: it.serialNumber || null,
              product: { type: it.productType || null },
              orderItemType: ITEM_TYPE.MAIN,
              productType: 1,
              vouchers: (it.vouchers || []).map((v) => ({
                voucherId: v.voucherId,
                voucherDetailId: v.voucherDetailId,
                voucherCode: v.voucherCode,
                voucherTypeDiscount: v.voucherTypeDiscount,
                voucherAmount: Number(v.voucherAmount) || 0,
              })),
            };
            // SP tặng kèm (nested) → flatten thành dòng riêng gắn parent.
            const gifts = (it.gifts || []).map((g) => ({
              id: g.id,
              _clientId: this.$uuid.v4(),
              belongOrderClientId: mainClientId,
              orderItemType: ITEM_TYPE.GIFT,
              checked: true,
              deleted: false,
              productId: g.productId,
              productName: g.productName,
              quantity: Number(g.quantity) || 1,
              sellingPrice: 0,
              sellingPriceMask: 0,
              productPrice: 0,
              discountType: 'AMOUNT',
              discountAmount: 0,
              totalAmount: 0,
              productChassisId: g.productChassisId || null,
              productSerialId: g.productSerialId || null,
              vouchers: [],
              product: { type: g.productType || null },
            }));
            // SP bán kèm (nested) → flatten dòng riêng gắn parent, GIỮ giá.
            const attachs = (it.attachs || []).map((a) => {
              const price = Number(a.productPrice) || 0;
              return {
                id: a.id,
                _clientId: this.$uuid.v4(),
                belongOrderClientId: mainClientId,
                orderItemType: ITEM_TYPE.ATTACH,
                checked: true,
                deleted: false,
                productId: a.productId,
                productName: a.productName,
                quantity: Number(a.quantity) || 1,
                sellingPrice: price,
                sellingPriceMask: price,
                productPrice: price,
                discountType: a.discountType || 'AMOUNT',
                discountAmount: Number(a.discountAmount) || 0,
                totalAmount: Number(a.totalAmount) || 0,
                productChassisId: a.productChassisId || null,
                productSerialId: a.productSerialId || null,
                // SP bán kèm có thể là xe (CHASSIS_ENGINE/SERIES) → cần loại SP để
                // hiện nút chọn số khung/seri + hiển thị SK/SM/seri nếu đã phân bổ.
                chassisNumber:
                  a.chassisNumber || a.productChassis?.chassisNumber || null,
                engineNumber:
                  a.engineNumber || a.productChassis?.engineNumber || null,
                serialNumber:
                  a.serialNumber || a.productSerial?.serialNumber || null,
                vouchers: [],
                product: { type: a.product?.type || a.productType || null },
              };
            });
            return [main, ...gifts, ...attachs];
          }),
          contractPaymentTerm: (data.paymentTerms || []).map((t) => ({
            id: t.id,
            name: t.name || '',
            totalAmount: Number(t.totalAmount) || 0,
            fromDate: t.fromDate
              ? moment(t.fromDate).format('DD/MM/YYYY')
              : null,
            toDate: t.toDate ? moment(t.toDate).format('DD/MM/YYYY') : null,
            description: t.description || '',
            termOrder: Number(t.termOrder) || 1,
          })),
        };
        this.contractEmployeeInfo = {
          saleId: data.saleId || null,
          saleName: data.saleName || '',
          shipperId: data.shipperId || null,
          shipperName: data.shipperName || '',
          // Phase B — 4 vai trò NV mở rộng (bán xe).
          technicalId: data.technicalId || null,
          technicalName: data.technicalName || '',
          cashierId: data.cashierId || null,
          cashierName: data.cashierName || '',
          managerId: data.managerId || null,
          managerName: data.managerName || '',
          assistantId: data.assistantId || null,
          assistantName: data.assistantName || '',
        };
        this.customerBusinessId = data.customerBusinessId || null;
        this.customerEmail = data.customerEmail || '';
        this.customerNote = data.customerNote || '';
        this.internalNote = data.internalNote || '';
        this.loadingComponent = true;
        // Sau khi mount xong, fetch detail Customer/Business để prefill legal section.
        if (
          data.customerType === CUSTOMER_TYPE.BUSSINESS &&
          data.customerBusinessId
        ) {
          this.loadBusinessDetail(data.customerBusinessId);
        } else if (data.customerId) {
          this.loadCustomerDetail(data.customerId);
        }
      } catch (err) {
        this.loadError =
          get(err, 'response.data.message') || 'Không tải được Đề nghị';
        this.loadingComponent = true;
      }
    },
    validate() {
      const c = this.contractData;
      if (!c.customerName) {
        return 'Vui lòng nhập tên khách hàng';
      }
      if (!c.customerPhone) {
        return 'Vui lòng nhập số điện thoại khách hàng';
      }
      if (!c.storeId) {
        return 'Vui lòng chọn cửa hàng';
      }
      if (!c.contractDetail.length) {
        return 'Cần ít nhất 1 sản phẩm';
      }
      const badItem = c.contractDetail.find(
        (it) =>
          !it.productId ||
          !it.productName ||
          !it.quantity ||
          Number(it.quantity) < 1,
      );
      if (badItem) {
        return 'Item thiếu thông tin (productId / số lượng)';
      }
      return null;
    },
    /**
     * Validate field pháp lý KH (cá nhân / DN). Fill `legalErrors` để hiện inline.
     * Trả về true nếu hợp lệ.
     */
    validateLegal() {
      this.legalErrors = {};
      const isBusiness =
        this.contractData.customerType === CUSTOMER_TYPE.BUSSINESS;
      if (isBusiness) {
        const legal = this.businessLegal;
        if (!(legal.representative || '').trim()) {
          this.$set(
            this.legalErrors,
            'representative',
            'Vui lòng nhập người đại diện',
          );
        }
        if (!(legal.repPosition || '').trim()) {
          this.$set(this.legalErrors, 'repPosition', 'Vui lòng nhập chức vụ');
        }
      } else {
        const legal = this.customerLegal;
        const cccdNo = (legal.cccdNo || '').trim();
        if (!cccdNo) {
          this.$set(this.legalErrors, 'cccdNo', 'Vui lòng nhập số CCCD/CMND');
        } else if (!/^\d{9,12}$/.test(cccdNo)) {
          this.$set(
            this.legalErrors,
            'cccdNo',
            'CCCD/CMND phải gồm 9-12 chữ số',
          );
        }
        if (!legal.cccdDate) {
          this.$set(this.legalErrors, 'cccdDate', 'Vui lòng chọn ngày cấp');
        }
        if (!(legal.cccdPlace || '').trim()) {
          this.$set(this.legalErrors, 'cccdPlace', 'Vui lòng nhập nơi cấp');
        }
      }
      return Object.keys(this.legalErrors).length === 0;
    },
    /**
     * Convert ISO/Date BE → 'DD/MM/YYYY' (định dạng datepicker).
     */
    toDpDate(value) {
      if (!value) return '';
      const m = moment(value);
      return m.isValid() ? m.format('DD/MM/YYYY') : '';
    },
    /**
     * Convert 'DD/MM/YYYY' (datepicker) → 'YYYY-MM-DD' (BE @IsDateString).
     * Trả null nếu rỗng để DTO @ValidateIf bỏ qua.
     */
    toBeDate(value) {
      if (!value) return null;
      const m = moment(value, 'DD/MM/YYYY', true);
      return m.isValid() ? m.format('YYYY-MM-DD') : null;
    },
    /**
     * Load Customer detail (cá nhân) → prefill customerLegal + lưu snapshot.
     * Date BE trả ISO → convert sang DD/MM/YYYY cho datepicker.
     */
    async loadCustomerDetail(customerId) {
      if (!customerId) return;
      try {
        const res = await ApiService.get(`customers/${customerId}`);
        const c = get(res, 'data.data') || get(res, 'data') || {};
        this.customerSnapshot = c;
        this.customerLegal = {
          cccdNo: c.cccdNo || '',
          cccdDate: this.toDpDate(c.cccdDate),
          cccdPlace: c.cccdPlace || '',
          phone2: c.phone2 || '',
          dob: this.toDpDate(c.dateOfBirth),
        };
        // Prefill Danh xưng (gender) nếu request không lưu — lấy từ Customer
        if (c.gender && !this.contractData.customerGender) {
          this.$set(this.contractData, 'customerGender', c.gender);
        }
        // Prefill email nếu chưa có
        if (c.email && !this.customerEmail) {
          this.customerEmail = c.email;
        }
        this.legalLoadedCustomerId = customerId;
      } catch (err) {
        // Không block flow — user vẫn nhập tay.
        // eslint-disable-next-line no-console
        console.error('loadCustomerDetail error', err);
      }
    },
    /**
     * Load Business detail → prefill businessLegal + snapshot.
     */
    async loadBusinessDetail(businessId) {
      if (!businessId) return;
      try {
        const res = await ApiService.get(`businesses/${businessId}/detail`);
        const b = get(res, 'data.data') || get(res, 'data') || {};
        this.businessSnapshot = b;
        this.businessLegal = {
          representative: b.representative || '',
          repPosition: b.repPosition || '',
          foundDate: this.toDpDate(b.foundDate),
          authorizationNo: b.authorizationNo || '',
          authorizationDate: this.toDpDate(b.authorizationDate),
        };
        // Prefill MST input cho Autosuggest (refactor v4 inline render).
        if (b.taxNo) this.businessTaxNoInput = b.taxNo;
        this.legalLoadedBusinessId = businessId;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('loadBusinessDetail error', err);
      }
    },
    /**
     * PUT /customers — DTO yêu cầu full body. Merge snapshot + legal mới.
     * Pattern legacy theo UpdateCustomer.vue: ApiService.put('customers', body) (no path).
     * Xem PIT-F-007.
     */
    async persistCustomerLegal() {
      const c = this.contractData;
      if (!c.customerId) return;
      const snap = this.customerSnapshot || {};
      const legal = this.customerLegal;
      const emailVal = (snap.email || this.customerEmail || '').trim();
      const data = {
        id: c.customerId,
        fullName: snap.fullName || c.customerName || '',
        phoneNumber: snap.phoneNumber || c.customerPhone || '',
        gender: c.customerGender || snap.gender || 'OTHER',
        dateOfBirth:
          this.toBeDate(legal.dob) ||
          (snap.dateOfBirth
            ? moment(snap.dateOfBirth).format('YYYY-MM-DD')
            : null),
        address: snap.address || c.customerAddress || '',
        note: snap.note || '',
        deleted: snap.deleted ? 1 : 0,
        // Field pháp lý mới
        phone2: (legal.phone2 || '').trim(),
        cccdNo: (legal.cccdNo || '').trim(),
        cccdDate: this.toBeDate(legal.cccdDate),
        cccdPlace: (legal.cccdPlace || '').trim(),
      };
      if (emailVal) data.email = emailVal;
      await ApiService.put('customers', data);
    },
    /**
     * PUT /businesses — DTO yêu cầu name/phoneNo. Merge snapshot + legal mới.
     */
    async persistBusinessLegal() {
      if (!this.customerBusinessId) return;
      const snap = this.businessSnapshot || {};
      const legal = this.businessLegal;
      const c = this.contractData;
      const emailVal = (snap.email || this.customerEmail || '').trim();
      const data = {
        id: this.customerBusinessId,
        name: snap.name || c.customerName || '',
        taxNo: snap.taxNo || '',
        phoneNo: snap.phoneNo || c.customerPhone || '',
        address: snap.address || c.customerAddress || '',
        note: snap.note || '',
        // Field pháp lý mới
        representative: (legal.representative || '').trim(),
        repPosition: (legal.repPosition || '').trim(),
        foundDate: this.toBeDate(legal.foundDate),
        authorizationNo: (legal.authorizationNo || '').trim(),
        authorizationDate: this.toBeDate(legal.authorizationDate),
      };
      if (emailVal) data.email = emailVal;
      await ApiService.put('businesses', data);
    },
    buildPayload() {
      const c = this.contractData;
      const details = c.contractDetail || [];
      // SP tặng kèm (orderItemType = GIFT) gom theo item chính (belongOrderClientId).
      const giftsByParent = {};
      details
        .filter((d) => d.orderItemType === ITEM_TYPE.GIFT)
        .forEach((g) => {
          const key = g.belongOrderClientId;
          if (!key) return;
          (giftsByParent[key] = giftsByParent[key] || []).push({
            productId: g.productId,
            productName: g.productName,
            quantity: Number(g.quantity) || 1,
            productChassisId: g.productChassisId || undefined,
            productSerialId: g.productSerialId || undefined,
          });
        });
      // SP bán kèm (orderItemType = ATTACH) gom theo item chính — GIỮ giá/CK.
      const attachsByParent = {};
      details
        .filter((d) => d.orderItemType === ITEM_TYPE.ATTACH)
        .forEach((a) => {
          const key = a.belongOrderClientId;
          if (!key) return;
          (attachsByParent[key] = attachsByParent[key] || []).push({
            productId: a.productId,
            productName: a.productName,
            productPrice:
              Number(unMaskPrice(a.sellingPriceMask) || a.productPrice) || 0,
            quantity: Number(a.quantity) || 1,
            discountType: a.discountType || 'AMOUNT',
            discountAmount: Number(unMaskPrice(a.discountAmount)) || 0,
            productChassisId: a.productChassisId || undefined,
            productSerialId: a.productSerialId || undefined,
          });
        });
      const payload = {
        customerType: c.customerType || CUSTOMER_TYPE.CUSTOMER,
        code: c.contractNumber || undefined,
        customerName: c.customerName,
        customerPhone: c.customerPhone,
        storeId: c.storeId,
        items: details
          .filter(
            (d) =>
              d.orderItemType !== ITEM_TYPE.GIFT &&
              d.orderItemType !== ITEM_TYPE.ATTACH,
          )
          .map((it) => ({
            productId: it.productId,
            productName: it.productName,
            productPrice: Number(it.productPrice) || 0,
            quantity: Number(it.quantity) || 1,
            discountType: it.discountType || 'AMOUNT',
            discountAmount: Number(unMaskPrice(it.discountAmount)) || 0,
            productChassisId: it.productChassisId || undefined,
            productSerialId: it.productSerialId || undefined,
            vouchers: it.vouchers || [],
            gifts: giftsByParent[it._clientId] || [],
            attachs: attachsByParent[it._clientId] || [],
          })),
      };
      if (c.customerType === CUSTOMER_TYPE.CUSTOMER) {
        if (c.customerId) payload.customerId = c.customerId;
      } else if (this.customerBusinessId) {
        payload.customerBusinessId = this.customerBusinessId;
      }
      // DN: gửi pháp lý để BE auto-create CustomerBusiness (taxNo NOT NULL).
      // LƯU Ý: MST/representative/... KHÔNG nằm trên raw `contractData` mà ở
      // `businessTaxNoInput` / `businessLegal.*` — phải đọc qua `customerProxy`
      // (computed map đúng), KHÔNG dùng `c`. (Bug v4: đọc c.customerMst=undefined.)
      if (c.customerType === CUSTOMER_TYPE.BUSSINESS) {
        const p = this.customerProxy;
        const mst = (p.customerMst || '').trim();
        if (mst) payload.customerMst = mst;
        if (p.customerRepresentative) {
          payload.customerRepresentative = p.customerRepresentative;
        }
        if (p.customerRepPosition) {
          payload.customerRepPosition = p.customerRepPosition;
        }
        const foundDate = this.toBeDate(p.customerFoundDate);
        if (foundDate) payload.customerFoundDate = foundDate;
        if (p.authorizationNo) payload.authorizationNo = p.authorizationNo;
        const authDate = this.toBeDate(p.authorizationDate);
        if (authDate) payload.authorizationDate = authDate;
      }
      if (this.customerEmail) payload.customerEmail = this.customerEmail;
      if (c.customerAddress) payload.customerAddress = c.customerAddress;
      if (this.customerNote) payload.customerNote = this.customerNote;
      // Danh xưng (Anh=1/Chị=2). Gửi qua ĐN để KH mới lưu đúng gender —
      // trước đây không gửi → BE default OTHER ("Khác"), phải sửa lại.
      if (
        c.customerType === CUSTOMER_TYPE.CUSTOMER &&
        c.customerGender != null
      ) {
        payload.customerGender = c.customerGender;
      }
      // Pháp lý KH cá nhân — gửi qua ĐN để KH mới có đủ CCCD/dob lúc convert
      // HĐMB (tránh CUSTOMER_INFO_INCOMPLETE). toBeDate: DD/MM/YYYY→YYYY-MM-DD.
      if (c.customerType === CUSTOMER_TYPE.CUSTOMER) {
        const legal = this.customerLegal || {};
        const cccdNo = (legal.cccdNo || '').trim();
        const cccdPlace = (legal.cccdPlace || '').trim();
        const cccdDate = this.toBeDate(legal.cccdDate);
        const dob = this.toBeDate(legal.dob);
        if (cccdNo) payload.customerCccdNo = cccdNo;
        if (cccdDate) payload.customerCccdDate = cccdDate;
        if (cccdPlace) payload.customerCccdPlace = cccdPlace;
        if (dob) payload.customerDob = dob;
      }
      if (c.saleId) payload.saleId = c.saleId;
      if (c.saleName) payload.saleName = c.saleName;
      // Follow-up Phase B — Shipper trên ĐN (đóng gap snapshot chain ĐN→HĐ).
      if (c.shipperId) payload.shipperId = c.shipperId;
      if (c.shipperName) payload.shipperName = c.shipperName;
      // Phase B — 4 vai trò NV mở rộng (bán xe). Gửi cả Id + Name. Optional.
      if (c.technicalId) payload.technicalId = c.technicalId;
      if (c.technicalName) payload.technicalName = c.technicalName;
      if (c.cashierId) payload.cashierId = c.cashierId;
      if (c.cashierName) payload.cashierName = c.cashierName;
      if (c.managerId) payload.managerId = c.managerId;
      if (c.managerName) payload.managerName = c.managerName;
      if (c.assistantId) payload.assistantId = c.assistantId;
      if (c.assistantName) payload.assistantName = c.assistantName;
      if (c.contractDate) {
        payload.contractDate = moment(c.contractDate, 'DD/MM/YYYY').format(
          'YYYY-MM-DD',
        );
      }
      if (c.expectedDeliveryDate) {
        payload.expectedDeliveryDate = moment(
          c.expectedDeliveryDate,
          'DD/MM/YYYY',
        ).format('YYYY-MM-DD');
      }
      if (c.paymentType) payload.paymentType = c.paymentType;
      if (c.shippingAddress) payload.shippingAddress = c.shippingAddress;
      if (this.internalNote) payload.internalNote = this.internalNote;
      // Request-level discount (Discount.vue component v-model contractData.totalDiscountAmount + .discountType)
      if (c.discountType) payload.discountType = c.discountType;
      const totalDiscountRaw = unMaskPrice(c.totalDiscountAmount);
      const totalDiscountNum = Number(totalDiscountRaw);
      if (!Number.isNaN(totalDiscountNum) && totalDiscountNum > 0) {
        payload.totalDiscountAmount = totalDiscountNum;
      } else {
        payload.totalDiscountAmount = 0;
      }
      if (c.paymentType === 'INSTALLMENT' && c.contractPaymentTerm.length) {
        payload.paymentTerms = c.contractPaymentTerm.map((t, idx) => {
          const p = {
            name: t.name,
            totalAmount: Number(unMaskPrice(t.totalAmount)) || 0,
            termOrder: Number(t.termOrder) || idx + 1,
          };
          if (t.fromDate) {
            p.fromDate = moment(t.fromDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
          }
          if (t.toDate) {
            p.toDate = moment(t.toDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
          }
          if (t.description) p.description = t.description;
          return p;
        });
      }
      return payload;
    },
    async onSubmit() {
      if (this.isFormLocked) return;
      this.submitError = '';
      const errMsg = this.validate();
      if (errMsg) {
        this.submitError = errMsg;
        makeToastFaile(errMsg);
        return;
      }
      // Validate legal (cá nhân: 3 field bắt buộc; DN: 2 field bắt buộc).
      if (!this.validateLegal()) {
        this.submitError = 'Vui lòng nhập đầy đủ thông tin pháp lý KH';
        makeToastFaile(this.submitError);
        return;
      }
      const payload = this.buildPayload();
      this.submitting = true;
      try {
        // Step 1 — Update Customer/Business với 4-5 field pháp lý mới.
        // Đây là điểm chuyển UX từ FE-002: pháp lý không còn ở modal Tạo HĐ
        // mà persist ngay khi tạo/sửa Đề nghị → modal Tạo HĐ chỉ chuyển trạng thái.
        const isBusiness =
          this.contractData.customerType === CUSTOMER_TYPE.BUSSINESS;
        if (isBusiness && this.customerBusinessId) {
          await this.persistBusinessLegal();
        } else if (!isBusiness && this.contractData.customerId) {
          await this.persistCustomerLegal();
        }
        // Nếu KH chưa có ID (KH mới chưa tạo qua autocomplete) → bỏ step legal.
        // Trường hợp này BE ContractRequest sẽ tự tạo Customer/Business từ
        // payload customerName/customerPhone — pháp lý sẽ phải nhập lại sau khi
        // KH có id (hạn chế Phase 1, ghi vào TODO docs).

        // Step 2 — Save Đề nghị (giữ logic cũ).
        if (this.isEdit) {
          // Resubmit: ĐN bị từ chối → sửa & lưu sẽ về pending (BE xử lý).
          const wasRejected =
            this.requestStatus === CONTRACT_REQUEST_STATUS.REJECTED;
          await ContractRequestService.update(this.id, payload);
          makeToastSuccess(
            wasRejected ? 'Đã gửi duyệt lại Đề nghị' : 'Đã cập nhật Đề nghị',
          );
          // Cập nhật: KHÔNG redirect ra list — load lại dữ liệu mới nhất tại chỗ.
          await this.loadDetail();
        } else {
          await ContractRequestService.create(payload);
          makeToastSuccess('Đã tạo Đề nghị');
          this.$router.push({ name: 'list-contract-request' });
        }
      } catch (err) {
        const msg =
          get(err, 'response.data.message') ||
          err.message ||
          'Lưu Đề nghị thất bại';
        this.submitError = msg;
        makeToastFaile(msg);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// Style local — đồng bộ với Upsert.vue legacy (.upsert-order)
.contract-request-upsert {
  .form-group {
    margin-bottom: 1rem;
  }

  // Linked contract banner — text trắng đậm, underline, contrast cao trên bg success
  .cr-linked-contract {
    color: #fff !important;
    font-weight: 700;
    text-decoration: underline;
    cursor: pointer;

    i {
      color: inherit !important;
      font-size: 11px;
    }

    &:hover {
      opacity: 0.85;
    }
  }
}
</style>
